<!-- Copyright 2019, Nathan Lecompte -->

<template>
    <div class="list" :aria-label="title">
		<!-- List -->
        <ul tabindex="0">
			<!-- List item(s) -->
			<li v-for="(item, index) in items" :key="item.id" @keydown.enter="onItemClick(item)" class="list-item" tabindex="0">
                <input 
					:checked="(type === 'checkbox') ? active.indexOf(index)>-1 : active===item"
					@change="onItemClick(item)"
					:disabled="(type === 'checkbox') ? (active.indexOf(index)>-1 && active.length<=minActiveItems) : false"
					:id="'s'+item.id"
					:type="type"
					name="instance">
				<!-- List item state indicator -->
				<span></span>
				<!-- List item label -->
                <label 
					:for="'s'+item.id" 
					:title="item.content+((item.desc) ? ' — '+item.desc: '')" 
					:aria-label="item.content+((item.desc) ? ' — '+item.desc: '')">
					{{ item.content }}
					<p v-if="item.desc">
						{{ ' — '+item.desc }}
					</p>
				</label>
				<!-- List item delete button -->
                <button 
					v-if="enableDelete && ((type === 'checkbox') ? active.indexOf(index)===-1 : active!=item)" 
					@keydown.enter="onDeleteClick(index)" 
					@click="onDeleteClick(index)" 
					:title="titleDelete" 
					:aria-label="titleDelete" 
					tabindex="0">
                    <svg viewBox="0 0 512 512" width="16" height="16" xmlns="http://www.w3.org/2000/svg">
						<path d="m437.5 386.6-130.6-130.6 130.6-130.6c14.1-14.1 14.1-36.8 0-50.9s-36.8-14.1-50.9 0l-130.6 130.6-130.6-130.6c-14.1-14.1-36.8-14.1-50.9 0s-14.1 36.8 0 50.9l130.6 130.6-130.6 130.6c-14.1 14.1-14.1 36.8 0 50.9s36.8 14.1 50.9 0l130.6-130.6 130.6 130.6c14.1 14.1 36.8 14.1 50.9 0 14-14.1 14-36.9 0-50.9z"/>
					</svg>
                </button>
            </li>
        </ul>
		
		<!-- List new item form -->
        <div class="list-new" v-if="enableNew" :aria-label="titleNew">
			<!-- List new item label (accessibility) -->
            <label for="s-new">{{ titleNew }}: </label>
			<!-- List new item input -->
            <input 
				v-model="newItem"
				@keyup.enter="onNewClick(newItem.toLowerCase())"
				id="s-new"
				name="new-instance" 
				type="text" 
				:title="titleNewInput" 
				:aria-label="titleNewInput"
				tabindex="0" 
				:placeholder="placeholderNew" 
				autocapitalize="off" 
				autocomplete="off" 
				autocorrect="off" 
				spellcheck="false">
            <!-- List new item button -->
			<button 
				@click="onNewClick(newItem.toLowerCase())"
				:title="titleNewButton" 
				:aria-label="titleNewButton" 
				tabindex="0">
                <svg viewBox="0 0 512 512" width="14" height="14" xmlns="http://www.w3.org/2000/svg">
					<path d="m291 470.6v-179.6h178.9c19.2 0 35.4-15.2 35.7-34.4.3-19.7-15.4-35.6-35-35.6h-179.6v-178.9c0-19.2-15.2-35.4-34.4-35.7-19.7-.3-35.6 15.4-35.6 35v179.6h-178.9c-19.2 0-35.4 15.2-35.7 34.4-.3 19.7 15.4 35.6 35 35.6h179.6v179.6c0 19.4 15.6 35 35 35 19.3-.1 35-15.7 35-35z"/>
				</svg>
            </button>
        </div>
    </div>
</template>

<script>
export default {
    props: {
        // Type of item-selection method (radio or checkbox)
        type: {
            type: String,
            required: true
        },
        // Title / Aria-label for list (accessibility)
        title: {
            type: String,
            required: true
        },

        // Items in list
        items: {
            type: Array,
            required: true
		},
        // Active item(s) in list
        active: {
            type: [Array, Object],
            required: true
		},
		// Min active items to keep selected (checkbox only)
		minActiveItems: Number,
        // Function on item click
        onItemClick: {
            type: Function,
            required: true
        },

        // Flag if item delete button is enabled
        enableDelete: {
            type: Boolean,
            required: true
        },
        // Function on item delete button click
        onDeleteClick: Function,
        // Title / Aria-label for item delete button (accessibility)
        titleDelete: String,

        // Flag if new item form is enabled
        enableNew: {
            type: Boolean,
            required: true
        },
        // Function on new item click
        onNewClick: Function,
        // Placeholder for new item input (accessibility)
        placeholderNew: String,
        // Title / Aria-label for new item form (accessibility)
        titleNew: String,
        // Title / Aria-label for new item input (accessibility)
        titleNewInput: String,
        // Title / Aria-label for new item button (accessibility)
        titleNewButton: String
    },
    data() {
        return {
            newItem: ''
        }
    }
}
</script>

<style>
/* List component */
.list ul {
	padding: 0;
	max-height: 254px;
	margin: 0 0 40px;
	overflow-y: scroll;
	outline: none;
	border: 1px solid rgba(var(--primaryColor), .05);
	border-radius: 2px;
	transition: var(--animationSpeed) ease
}
.list ul:focus {
	border: 1px solid rgba(var(--accentColor), .2)
}
.list .list-item {
	display: flex;
	position: relative;
	padding-left: 10px;
	vertical-align: middle;
	font-size: .95em;
	font-weight: 400;
	color: rgba(var(--primaryColor), var(--primaryOpacity));
	outline: none;
	-webkit-tap-highlight-color: transparent;
	transition: calc(var(--animationSpeed)/2) ease
}
.list .list-item:hover {
	color: rgb(var(--primaryColor))
}
.list .list-item:active,
.list .list-item:focus {
	font-weight: 600;
	color: rgb(var(--accentColor));
	background: rgba(var(--accentColor), .025)
}
.list .list-item label {
	display: block;
	padding: 20px 20px 20px 45px;
	margin-left: -35px;
	width: 100%;
	cursor: pointer;
	white-space: nowrap;
	overflow: hidden;
	outline: none;
	text-overflow: ellipsis
}
.list .list-item label p {
	display: inline;
	color: rgba(var(--primaryColor), var(--primaryOpacity));
	font-weight: 400;
	margin: 0 0 0 5px !important
}
.list .list-item button {
	display: block;
	float: right;
	background: none;
	border: none;
	opacity: 0;
	padding: 0 20px;
	cursor: pointer;
	outline: none;
	-webkit-tap-highlight-color: transparent;
	transition: calc(var(--animationSpeed)/2) ease
}
.list .list-item button:active,
.list .list-item button:focus {
	background: rgba(var(--accentColor),.025)
}
.list .list-item:hover button, 
.list .list-item button:focus {
	opacity: 1
}
.list .list-item input {
	display: none
}
.list .list-item span {
	display: inline-block;
	background: rgba(var(--primaryColor), .05);
	border: 1px solid rgb(var(--primaryColor), .05);
	border-radius: 20px;
	width: 13px;
	height: 13px;
	min-width: 13px;
	min-height: 13px;
	max-width: 13px;
	max-height: 13px;
	margin: 23px 0 0 10px;
	transition: calc(var(--animationSpeed)/2) ease
}
.list .list-item span::after {
	display: block;
	position: absolute;
	content: '';
	border-radius: 20px;
	width: 5px;
	height: 5px;
	top: 28px;
	left: 25px
}
.list .list-item input:checked ~ span, 
.list .list-item input:checked:hover ~ span {
	background: rgb(var(--accentColor))
}
.list .list-item input:hover ~ span::after, 
.list .list-item input:checked ~ span::after, 
.list .list-item input:checked:hover ~ span::after {
	background: rgb(var(--backgroundColor));
	box-shadow: 0 0 0 1px rgba(var(--primaryColor), .05)
}
.list .list-item input:checked ~ label, 
.list .list-item input:checked:hover ~ label {
	font-weight: 600;
	color: rgb(var(--accentColor))
}
.list-new {
	display: flex;
	border: 1px solid rgba(var(--primaryColor), .05);
	border-radius: 0 0 2px 2px;
	border-width: 0 1px 1px;
	margin-top: -41px !important
}
.list-new input {
	background: none;
	border: none;
	padding: 20px;
	font: 400 .95em var(--primaryFont);
	color: rgb(var(--primaryColor));
	width: 100%;
	outline: none;
	-webkit-tap-highlight-color: transparent
}
.list-new input:focus::placeholder {
	opacity: .5
}
.list-new button {
	padding: 0 18px;
	margin: 6px 5px 5px;
	border-radius: 2px;
	border: none;
	background: none;
	cursor: pointer;
	outline: none;
	-webkit-tap-highlight-color: transparent;
	transition: var(--animationSpeed) ease
}
.list-new button:active,
.list-new button:focus {
	background: rgba(var(--accentColor), .05)
}
.list-new label {
	position: absolute;
	clip: rect(0px, 0px, 0px, 0px)
}
</style>